import { useState, useEffect } from 'react';
import {
    Key,
    Save,
    Trash2,
    Plus,
    Edit2,
    Check,
    X,
    Eye,
    EyeOff,
    HardDrive,
    Cloud,
    Lock,
    LogOut,
} from 'lucide-react';
import { settingsApi, authApi, chatApi } from '../lib/api';
import { storage } from '../lib/utils';
import { useI18n } from '../i18n';
import type { UserSettings, SystemPrompt } from '../lib/types';

export default function Settings() {
    const { tf: t } = useI18n();
    const [settings, setSettings] = useState<UserSettings | null>(null);
    const [prompts, setPrompts] = useState<SystemPrompt[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Form states
    const [apiKey, setApiKey] = useState('');
    const [showApiKey, setShowApiKey] = useState(false);
    const [autoSaveR2, setAutoSaveR2] = useState(false);

    // R2 Configuration
    const [r2Config, setR2Config] = useState({
        account_id: '',
        access_key_id: '',
        secret_access_key: '',
        bucket_name: '',
    });
    const [showR2Secret, setShowR2Secret] = useState(false);

    // Prompt editing
    const [editingPrompt, setEditingPrompt] = useState<SystemPrompt | null>(null);
    const [newPrompt, setNewPrompt] = useState({ title: '', content: '' });
    const [showNewPrompt, setShowNewPrompt] = useState(false);

    // Password change
    const [passwordForm, setPasswordForm] = useState({
        current: '',
        new: '',
        confirm: '',
    });
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    // Load settings
    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        setIsLoading(true);
        try {
            const [settingsRes, promptsRes] = await Promise.all([
                settingsApi.get(),
                settingsApi.getPrompts(),
            ]);

            if (settingsRes.success) {
                setSettings(settingsRes.settings);
                setAutoSaveR2(settingsRes.settings.auto_save_r2);
                setR2Config(prev => ({
                    ...prev,
                    account_id: settingsRes.settings.r2_account_id || '',
                    bucket_name: settingsRes.settings.r2_bucket_name || '',
                }));
            }

            if (promptsRes.success) {
                setPrompts(promptsRes.prompts);
            }
        } catch (error) {
            console.error('Failed to load settings:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Save API key
    const handleSaveApiKey = async () => {
        if (!apiKey.trim()) return;

        setIsSaving(true);
        try {
            const response = await settingsApi.update({ api_key: apiKey });
            if (response.success) {
                setApiKey('');
                setSettings((prev) => prev ? { ...prev, has_api_key: true } : null);
                alert(t('settings.apiKeySaved'));
            }
        } catch (error) {
            console.error('Failed to save API key:', error);
            alert(t('common.error'));
        } finally {
            setIsSaving(false);
        }
    };

    // Remove API key
    const handleRemoveApiKey = async () => {
        if (!confirm(t('common.confirm'))) return;

        setIsSaving(true);
        try {
            const response = await settingsApi.update({ api_key: '' });
            if (response.success) {
                setSettings((prev) => prev ? { ...prev, has_api_key: false } : null);
            }
        } catch (error) {
            console.error('Failed to remove API key:', error);
        } finally {
            setIsSaving(false);
        }
    };

    // Save R2 Configuration
    const handleSaveR2Config = async () => {
        if (!r2Config.account_id || !r2Config.access_key_id ||
            !r2Config.secret_access_key || !r2Config.bucket_name) {
            alert(t('common.error')); // Using generic error for empty fields
            return;
        }

        setIsSaving(true);
        try {
            const response = await settingsApi.update({
                r2_account_id: r2Config.account_id,
                r2_access_key_id: r2Config.access_key_id,
                r2_secret_access_key: r2Config.secret_access_key,
                r2_bucket_name: r2Config.bucket_name,
            });
            if (response.success) {
                setR2Config(prev => ({ ...prev, secret_access_key: '' }));
                setSettings((prev) => prev ? {
                    ...prev,
                    r2_configured: true,
                    r2_account_id: r2Config.account_id,
                    r2_bucket_name: r2Config.bucket_name,
                } : null);
                alert(t('settings.r2SaveSuccess'));
            }
        } catch (error) {
            console.error('Failed to save R2 config:', error);
            alert(t('common.error'));
        } finally {
            setIsSaving(false);
        }
    };

    // Toggle auto-save
    const handleToggleAutoSave = async () => {
        const newValue = !autoSaveR2;
        setAutoSaveR2(newValue);

        try {
            await settingsApi.update({ auto_save_r2: newValue });
        } catch (error) {
            console.error('Failed to update auto-save:', error);
            setAutoSaveR2(!newValue); // Revert
        }
    };

    // Create prompt
    const handleCreatePrompt = async () => {
        if (!newPrompt.title.trim() || !newPrompt.content.trim()) return;

        try {
            const response = await settingsApi.createPrompt(newPrompt);
            if (response.success && response.prompt) {
                setPrompts((prev) => [response.prompt, ...prev]);
                setNewPrompt({ title: '', content: '' });
                setShowNewPrompt(false);
            }
        } catch (error) {
            console.error('Failed to create prompt:', error);
        }
    };

    // Update prompt
    const handleUpdatePrompt = async (prompt: SystemPrompt) => {
        try {
            const response = await settingsApi.updatePrompt(prompt.id, {
                title: prompt.title,
                content: prompt.content,
                is_active: prompt.is_active,
            });
            if (response.success && response.prompt) {
                setPrompts((prev) =>
                    prev.map((p) => (p.id === prompt.id ? response.prompt : p))
                );
                setEditingPrompt(null);
            }
        } catch (error) {
            console.error('Failed to update prompt:', error);
        }
    };

    // Delete prompt
    const handleDeletePrompt = async (id: number) => {
        if (!confirm(t('settings.deletePromptConfirm'))) return;

        try {
            await settingsApi.deletePrompt(id);
            setPrompts((prev) => prev.filter((p) => p.id !== id));
        } catch (error) {
            console.error('Failed to delete prompt:', error);
        }
    };

    // Clear local cache
    const handleClearCache = () => {
        if (confirm(t('settings.clearCacheConfirm'))) {
            storage.clear();
            alert(t('settings.cacheClearedRefresh'));
            window.location.reload();
        }
    };

    // Export all chats
    const handleExportAll = async () => {
        try {
            const blob = await chatApi.exportAll();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `all-chats-${new Date().toISOString().split('T')[0]}.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
            alert(t('settings.backupFailed'));
        }
    };

    // Import all chats
    const handleImportAll = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const text = await file.text();
            const data = JSON.parse(text);
            const response = await chatApi.importAll(data);
            if (response.success) {
                alert(response.message || t('settings.importSuccess'));
                window.location.reload();
            } else {
                alert(t('settings.importFailed'));
            }
        } catch (error) {
            console.error('Import failed:', error);
            alert(t('settings.importFailed'));
        }
        e.target.value = '';
    };

    // Change password
    const handleChangePassword = async () => {
        if (!passwordForm.current || !passwordForm.new || !passwordForm.confirm) {
            alert(t('common.error'));
            return;
        }

        if (passwordForm.new !== passwordForm.confirm) {
            alert('새 비밀번호가 일치하지 않습니다.');
            return;
        }

        if (passwordForm.new.length < 8) {
            alert(t('common.error'));
            return;
        }

        setIsChangingPassword(true);
        try {
            const response = await authApi.changePassword(passwordForm.current, passwordForm.new);
            if (response.success) {
                setPasswordForm({ current: '', new: '', confirm: '' });
                alert(t('auth.passwordChanged'));
            } else {
                alert(response.message || t('common.error'));
            }
        } catch (error) {
            console.error('Password change error:', error);
            alert(t('common.error'));
        } finally {
            setIsChangingPassword(false);
        }
    };

    // Invalidate all sessions
    const handleInvalidateSessions = async () => {
        if (!confirm(t('common.confirm'))) return;

        try {
            await authApi.invalidateSessions();
            alert(t('auth.allSessionsInvalidated'));
            window.location.href = '/login';
        } catch (error) {
            console.error('Invalidate sessions error:', error);
            alert('세션 무효화에 실패했습니다.');
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="spinner text-accent-500 w-8 h-8" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto p-6">
            <h1 className="text-2xl font-bold text-dark-100 mb-6">{t('settings.title')}</h1>

            {/* API Key Section */}
            <section className="card mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center">
                        <Key className="w-5 h-5 text-accent-400" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-dark-100">{t('settings.apiKey')}</h2>
                        <p className="text-sm text-dark-400">
                            {t('settings.apiKeyDesc')}
                        </p>
                    </div>
                </div>

                {settings?.has_api_key ? (
                    <div className="flex items-center justify-between p-4 bg-dark-800 rounded-lg">
                        <div className="flex items-center gap-2">
                            <Check className="w-5 h-5 text-green-400" />
                            <span className="text-dark-200">{t('settings.apiKeySaved')}</span>
                        </div>
                        <button
                            onClick={handleRemoveApiKey}
                            disabled={isSaving}
                            className="btn-danger text-sm py-1.5"
                        >
                            <Trash2 className="w-4 h-4" />
                            {t('common.delete')}
                        </button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="relative">
                            <input
                                type={showApiKey ? 'text' : 'password'}
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder={t('settings.apiKeyPlaceholder')}
                                className="input pr-20"
                            />
                            <button
                                type="button"
                                onClick={() => setShowApiKey(!showApiKey)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-dark-200"
                            >
                                {showApiKey ? (
                                    <EyeOff className="w-5 h-5" />
                                ) : (
                                    <Eye className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                        <button
                            onClick={handleSaveApiKey}
                            disabled={!apiKey.trim() || isSaving}
                            className="btn-primary"
                        >
                            <Save className="w-4 h-4" />
                            {t('common.save')}
                        </button>
                    </div>
                )}
            </section>

            {/* R2 Configuration Section */}
            <section className="card mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                        <Cloud className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-dark-100">{t('settings.r2Config')}</h2>
                        <p className="text-sm text-dark-400">
                            {t('settings.r2ConfigDesc')}
                        </p>
                    </div>
                </div>

                {settings?.r2_configured ? (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-4 bg-dark-800 rounded-lg">
                            <div className="flex items-center gap-2">
                                <Check className="w-5 h-5 text-green-400" />
                                <div>
                                    <span className="text-dark-200">{t('settings.r2Configured')}</span>
                                    <p className="text-sm text-dark-500">
                                        버킷: {settings.r2_bucket_name}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <p className="text-sm text-dark-500">
                            {t('settings.r2ChangeHint')}
                        </p>
                    </div>
                ) : null}

                <div className="space-y-3 mt-4">
                    <input
                        type="text"
                        value={r2Config.account_id}
                        onChange={(e) => setR2Config(prev => ({ ...prev, account_id: e.target.value }))}
                        placeholder="Account ID"
                        className="input"
                    />
                    <input
                        type="text"
                        value={r2Config.access_key_id}
                        onChange={(e) => setR2Config(prev => ({ ...prev, access_key_id: e.target.value }))}
                        placeholder="Access Key ID"
                        className="input"
                    />
                    <div className="relative">
                        <input
                            type={showR2Secret ? 'text' : 'password'}
                            value={r2Config.secret_access_key}
                            onChange={(e) => setR2Config(prev => ({ ...prev, secret_access_key: e.target.value }))}
                            placeholder="Secret Access Key"
                            className="input pr-12"
                        />
                        <button
                            type="button"
                            onClick={() => setShowR2Secret(!showR2Secret)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-dark-200"
                        >
                            {showR2Secret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>
                    <input
                        type="text"
                        value={r2Config.bucket_name}
                        onChange={(e) => setR2Config(prev => ({ ...prev, bucket_name: e.target.value }))}
                        placeholder="Bucket Name"
                        className="input"
                    />
                    <button
                        onClick={handleSaveR2Config}
                        disabled={isSaving}
                        className="btn-primary"
                    >
                        <Save className="w-4 h-4" />
                        {t('common.save')}
                    </button>
                </div>
            </section>

            {/* Auto-save Section */}
            <section className="card mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                            <HardDrive className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-dark-100">{t('settings.autoSave')}</h2>
                            <p className="text-sm text-dark-400">
                                {t('settings.autoSaveDesc')}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleToggleAutoSave}
                        disabled={!settings?.r2_configured}
                        className={`relative w-12 h-6 rounded-full transition-colors ${autoSaveR2 && settings?.r2_configured ? 'bg-accent-500' : 'bg-dark-700'
                            } ${!settings?.r2_configured ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <span
                            className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${autoSaveR2 && settings?.r2_configured ? 'translate-x-7' : 'translate-x-1'
                                }`}
                        />
                    </button>
                </div>
                {!settings?.r2_configured && (
                    <p className="text-sm text-yellow-400 mt-2">
                        ⚠ 자동 저장을 사용하려면 먼저 R2를 설정하세요.
                    </p>
                )}
            </section>

            {/* Security Section */}
            <section className="card mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                        <Lock className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-dark-100">{t('settings.security')}</h2>
                        <p className="text-sm text-dark-400">
                            {t('settings.securityDesc')}
                        </p>
                    </div>
                </div>

                <div className="space-y-4">
                    {/* Password Change */}
                    <div className="space-y-3">
                        <h3 className="text-sm font-medium text-dark-200">{t('auth.changePassword')}</h3>
                        <input
                            type="password"
                            value={passwordForm.current}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
                            placeholder={t('auth.currentPassword')}
                            className="input"
                        />
                        <input
                            type="password"
                            value={passwordForm.new}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, new: e.target.value }))}
                            placeholder={t('auth.newPassword')}
                            className="input"
                        />
                        <input
                            type="password"
                            value={passwordForm.confirm}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                            placeholder={t('auth.confirmPassword')}
                            className="input"
                        />
                        <button
                            onClick={handleChangePassword}
                            disabled={isChangingPassword}
                            className="btn-primary"
                        >
                            <Lock className="w-4 h-4" />
                            {isChangingPassword ? t('auth.changingPassword') : t('auth.changePassword')}
                        </button>
                        <p className="text-xs text-dark-500">
                            {t('settings.passwordChangeHint')}
                        </p>
                    </div>

                    {/* Session Invalidation */}
                    <div className="pt-4 border-t border-dark-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-medium text-dark-200">{t('auth.invalidateAllSessions')}</h3>
                                <p className="text-xs text-dark-500">
                                    {t('settings.sessionLogoutHint')}
                                </p>
                            </div>
                            <button
                                onClick={handleInvalidateSessions}
                                className="btn-secondary text-red-400 border-red-400/30 hover:bg-red-400/10"
                            >
                                <LogOut className="w-4 h-4" />
                                {t('auth.invalidateAllSessions')}
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* System Prompts Section */}
            <section className="card mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-dark-100">{t('settings.systemPrompts')}</h2>
                    <button
                        onClick={() => setShowNewPrompt(true)}
                        className="btn-secondary text-sm py-1.5"
                    >
                        <Plus className="w-4 h-4" />
                        {t('common.add')}
                    </button>
                </div>

                {/* New prompt form */}
                {showNewPrompt && (
                    <div className="p-4 bg-dark-800 rounded-lg mb-4">
                        <input
                            type="text"
                            value={newPrompt.title}
                            onChange={(e) => setNewPrompt((prev) => ({ ...prev, title: e.target.value }))}
                            placeholder={t('settings.promptTitle')}
                            className="input mb-3"
                        />
                        <textarea
                            value={newPrompt.content}
                            onChange={(e) => setNewPrompt((prev) => ({ ...prev, content: e.target.value }))}
                            placeholder={t('settings.promptContent')}
                            rows={4}
                            className="input resize-none mb-3"
                        />
                        <div className="flex gap-2">
                            <button onClick={handleCreatePrompt} className="btn-primary text-sm">
                                <Check className="w-4 h-4" />
                                {t('common.add')}
                            </button>
                            <button
                                onClick={() => {
                                    setShowNewPrompt(false);
                                    setNewPrompt({ title: '', content: '' });
                                }}
                                className="btn-secondary text-sm"
                            >
                                <X className="w-4 h-4" />
                                {t('common.cancel')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Prompts list */}
                <div className="space-y-3">
                    {prompts.length === 0 ? (
                        <p className="text-dark-500 text-center py-4">
                            등록된 시스템 프롬프트가 없습니다
                        </p>
                    ) : (
                        prompts.map((prompt) => (
                            <div key={prompt.id} className="p-4 bg-dark-800 rounded-lg">
                                {editingPrompt?.id === prompt.id ? (
                                    <>
                                        <input
                                            type="text"
                                            value={editingPrompt.title}
                                            onChange={(e) =>
                                                setEditingPrompt((prev) =>
                                                    prev ? { ...prev, title: e.target.value } : null
                                                )
                                            }
                                            className="input mb-2"
                                        />
                                        <textarea
                                            value={editingPrompt.content}
                                            onChange={(e) =>
                                                setEditingPrompt((prev) =>
                                                    prev ? { ...prev, content: e.target.value } : null
                                                )
                                            }
                                            rows={4}
                                            className="input resize-none mb-2"
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleUpdatePrompt(editingPrompt)}
                                                className="btn-primary text-sm"
                                            >
                                                <Check className="w-4 h-4" />
                                                {t('common.save')}
                                            </button>
                                            <button
                                                onClick={() => setEditingPrompt(null)}
                                                className="btn-secondary text-sm"
                                            >
                                                <X className="w-4 h-4" />
                                                {t('common.cancel')}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="font-medium text-dark-100">{prompt.title}</h3>
                                            <div className="flex items-center gap-2">
                                                {prompt.is_active && (
                                                    <span className="badge-success">{t('admin.models.active')}</span>
                                                )}
                                                <button
                                                    onClick={() => setEditingPrompt(prompt)}
                                                    className="btn-icon"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeletePrompt(prompt.id)}
                                                    className="btn-icon text-red-400"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <p className="text-sm text-dark-400 line-clamp-2">
                                            {prompt.content}
                                        </p>
                                    </>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </section>


            {/* Chat Backup Section */}
            <section className="card">
                <h2 className="text-lg font-semibold text-dark-100 mb-4">{t('settings.chatBackup')}</h2>
                <div className="flex flex-wrap gap-3">
                    <button onClick={handleExportAll} className="btn-secondary">
                        <Save className="w-4 h-4" />
                        {t('settings.exportAll')}
                    </button>
                    <label className="btn-secondary cursor-pointer">
                        <Plus className="w-4 h-4" />
                        {t('settings.importAll')}
                        <input
                            type="file"
                            accept=".json"
                            onChange={handleImportAll}
                            className="hidden"
                        />
                    </label>
                </div>
                <p className="text-sm text-dark-500 mt-3">
                    {t('settings.backupDesc')}
                </p>
            </section>

            {/* Clear Cache Section */}
            <section className="card">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-dark-100">{t('settings.clearCache')}</h2>
                        <p className="text-sm text-dark-400">
                            {t('settings.clearCacheDesc')}
                        </p>
                    </div>
                    <button onClick={handleClearCache} className="btn-danger">
                        <Trash2 className="w-4 h-4" />
                        {t('settings.clearCache')}
                    </button>
                </div>
            </section>
        </div>
    );
}
